import * as React from "react";
import { RingsBackground } from "@/components/ringsBackground/ringsBackground";
import { cn } from "@/lib/utils";
import styles from "./heroBackdrop.module.css";

/* Bottom fade so the lens dissolves into the content below instead of
   ending in a hard edge. Lives here because this component is the only
   thing that renders the hero's rings. */
const ringsBottomFade: React.CSSProperties = {
  WebkitMaskImage:
    "linear-gradient(to bottom, black 50%, rgba(0, 0, 0, 0.5) 72%, transparent 90%)",
  maskImage:
    "linear-gradient(to bottom, black 50%, rgba(0, 0, 0, 0.5) 72%, transparent 90%)",
};

export interface HeroBackdropProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** Content rendered above the rings. */
  children: React.ReactNode;
  /**
   * Cancel the layout's 64px navbar padding so the rings run under the
   * fixed navbar. Correct for a page's first section; turn off if the
   * hero sits partway down a page.
   */
  pullUnderNavbar?: boolean;
  /**
   * Emit `data-hero-section`, which `Navbar` reads to hide its wordmark
   * while the hero is in view and fade it in past ~70% of it.
   */
  markAsHero?: boolean;
  className?: string;
}

/**
 * The full-bleed hero surface — rings lens behind, content above.
 *
 * Shared by the `section.hero-section` block and any card section using
 * the `hero` custom ID, so both render an identically sized lens.
 */
const HeroBackdrop = React.forwardRef<HTMLDivElement, HeroBackdropProps>(
  (
    {
      children,
      pullUnderNavbar = true,
      markAsHero = true,
      className,
      ...props
    },
    ref,
  ) => (
    <div
      ref={ref}
      className={cn(
        styles.root,
        pullUnderNavbar ? styles.pullUnderNavbar : undefined,
        className,
      )}
      {...(markAsHero ? { "data-hero-section": "" } : {})}
      {...props}
    >
      <RingsBackground washless style={ringsBottomFade} />
      <div className={styles.content}>{children}</div>
    </div>
  ),
);

HeroBackdrop.displayName = "HeroBackdrop";

export { HeroBackdrop };
